import { useRef, useEffect } from 'react';
import { useCssVariableValue } from "../../../hooks";

type CircleLoaderProps = {
  size?: "small" | "large";
  variant?: "primary" | "secondary";
};

export const CircleLoader = ({
  size = "large",
  variant = "primary",
}: CircleLoaderProps) => {

  const numeralSize = size === "small" ? 40 : 80;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const primaryActive = useCssVariableValue('--primary');
  const secondaryActive = useCssVariableValue('--secondary-highlight');
  const active = variant === "primary" ? primaryActive : secondaryActive;
  const primaryMuted = useCssVariableValue('--primary-muted');
  const secondaryMuted = useCssVariableValue('--secondary');
  const muted = variant === "primary" ? primaryMuted : secondaryMuted;

  const waveRef = useRef({
    phase: 0,
    speed: 0.04,
    amplitude: numeralSize / 16,
    waves: size === "small" ? 6 : 8,
  });
  const rotationRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const padding = 20;
    const canvasSize = numeralSize + padding * 2;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(canvasSize * dpr);
    canvas.height = Math.round(canvasSize * dpr);
    canvas.style.width = `${canvasSize}px`;
    canvas.style.height = `${canvasSize}px`;
    ctx.scale(dpr, dpr);

    const lineWidth = numeralSize / 12;
    const baseRadius = (numeralSize - lineWidth * 2) / 2;
    const center = canvasSize / 2;

    let animationId: number;

    const draw = () => {
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      const wave = waveRef.current;

      // Skip drawing until theme colors resolve so we never flash black.
      if (!muted || !active) {
        animationId = requestAnimationFrame(draw);
        return;
      }

      // Background circle (muted) - smooth/straight
      ctx.beginPath();
      ctx.arc(center, center, baseRadius, 0, Math.PI * 2);
      ctx.strokeStyle = muted;
      ctx.lineWidth = lineWidth;
      ctx.stroke();

      // Wavy snake (active) - strong wave at head, fades to straight at tail
      const snakeLength = Math.PI * 1.5;
      const steps = 80;

      ctx.beginPath();
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const angle = rotationRef.current + t * snakeLength;
        
        // Wave is strongest at head (t=1), fades to straight at tail (t=0)
        const fade = Math.min(t * 2, 1);
        
        const waveOffset = wave.amplitude * fade * Math.sin(angle * wave.waves + wave.phase);
        const r = baseRadius + waveOffset;
        const x = center + r * Math.cos(angle);
        const y = center + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = active;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.stroke();

      wave.phase += wave.speed;
      rotationRef.current += 0.03;
      animationId = requestAnimationFrame(draw);
    };

    animationId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationId);
  }, [numeralSize, active, muted]);

  return (
    <canvas
      ref={canvasRef}
      className="circle-loader"
      style={{
        display: 'block',
      }}
    />
  );
};
